import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useOutlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Bell, ChevronRight, ChevronsUpDown, Clock3, Inbox, LogOut, Mail, Menu, Moon, PanelLeftClose, PanelLeftOpen, Plus, Search, Settings, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { adminDashboard } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Seo } from "@/components/shared/Seo";
import { QUICK_ACTIONS, currentNavItem, navGroupsFor } from "@/components/admin/nav";
import { SidebarNav } from "@/components/admin/SidebarNav";
import { DirtyProvider, useGuardedNavigate } from "@/components/admin/DirtyGuard";

const COLLAPSE_KEY = "admin.sidebarCollapsed";
const THEME_KEY = "admin.theme";

const initials = (email = "") => (email.split("@")[0] || "?").slice(0, 2).toUpperCase();

/** Workspace card at the top of the sidebar – mark + name, doubles as link to the oversikt. */
const Workspace = ({ collapsed = false, onClick }) => {
  const { isAdmin } = useAuth();
  return (
  <Link to="/admin" onClick={onClick} className={cn("group flex min-w-0 items-center gap-2.5 rounded-xl p-1.5 text-paper transition-colors hover:bg-paper/[0.06]", collapsed && "justify-center")} data-testid="admin-logo-link" aria-label="Til oversikta">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-sol font-display text-[13px] font-semibold tracking-tight text-ink shadow-[0_0_0_3px_hsl(var(--sol)/0.18)]">HK</span>
    {!collapsed && (
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate font-display text-[14px] font-semibold tracking-[-0.01em]">Halsnøy Kulturhus</span>
        <span className="block truncate text-[11.5px] text-paper/55" data-testid="admin-workspace-role">{isAdmin ? "Admin · styret" : "Admin · brukar"}</span>
      </span>
    )}
    {!collapsed && <ChevronsUpDown className="h-4 w-4 shrink-0 text-paper/40 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />}
  </Link>
  );
};

const UserMenu = ({ collapsed = false, onNavigate }) => {
  const { admin, logout, isAdmin } = useAuth();
  const go = useGuardedNavigate();
  const trigger = (
    <button type="button" className={cn("flex h-12 w-full items-center gap-2.5 rounded-xl px-1.5 text-left text-paper transition-colors hover:bg-paper/[0.06]", collapsed && "justify-center px-0")} data-testid="admin-user-menu-button" aria-label="Brukarmeny">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-paper/10 text-[12px] font-semibold text-paper ring-1 ring-paper/15" aria-hidden="true">{initials(admin?.email)}</span>
      {!collapsed && (
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-[13px] font-medium" data-testid="admin-user-email">{admin?.email}</span>
          <span className="block text-[11px] text-paper/50" data-testid="admin-user-role">{isAdmin ? "Administrator" : "Brukar"}</span>
        </span>
      )}
      {!collapsed && <Settings className="h-4 w-4 shrink-0 text-paper/45" aria-hidden="true" />}
    </button>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-60 bg-popover">
        <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">{admin?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => { go("/admin/innstillingar"); onNavigate?.(); }} data-testid="admin-user-menu-settings">{isAdmin ? "Innstillingar og passord" : "Byt passord"}</DropdownMenuItem>
        <DropdownMenuItem asChild><a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site-link">Sjå nettsida <ArrowUpRight className="ml-auto h-3.5 w-3.5" /></a></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout()} data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Logg ut</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const CommandPalette = ({ open, onOpenChange }) => {
  const go = useGuardedNavigate();
  const { isAdmin } = useAuth();
  const run = (to) => { onOpenChange(false); go(to); };
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Gå til side eller handling…" data-testid="admin-command-input" />
      <CommandList data-testid="admin-command-list">
        <CommandEmpty>Ingen treff.</CommandEmpty>
        <CommandGroup heading="Handlingar">
          {QUICK_ACTIONS.map((a) => <CommandItem key={a.id} value={`handling ${a.label}`} onSelect={() => run(a.to)} data-testid={`admin-command-${a.id}`}><a.icon strokeWidth={1.75} /> {a.label}</CommandItem>)}
        </CommandGroup>
        <CommandSeparator />
        {navGroupsFor(isAdmin).map((g) => (
          <CommandGroup key={g.label || "oversikt"} heading={g.label || "Oversikt"}>
            {g.items.map((i) => <CommandItem key={i.to} value={`${i.label} ${i.description || ""}`} onSelect={() => run(i.to)} data-testid={`admin-command-nav-${i.id}`}><i.icon strokeWidth={1.75} /> <span>{i.label}</span>{i.description && <span className="ml-auto truncate text-xs text-muted-foreground">{i.description}</span>}</CommandItem>)}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

const Notifications = ({ counts }) => {
  const go = useGuardedNavigate();
  const total = (counts.bookings || 0) + (counts.messages || 0) + (counts.tickets || 0);
  const items = [
    counts.bookings > 0 && { id: "bookings", icon: Inbox, title: `${counts.bookings} ${counts.bookings === 1 ? "ny bookingførespurnad" : "nye bookingførespurnader"}`, sub: "Godkjenn eller avvis", to: "/admin/bookingar" },
    counts.messages > 0 && { id: "messages", icon: Mail, title: `${counts.messages} ${counts.messages === 1 ? "ulest melding" : "uleste meldingar"}`, sub: "Frå kontaktskjemaet", to: "/admin/meldingar" },
    counts.tickets > 0 && { id: "tickets", icon: Clock3, title: `${counts.tickets} ${counts.tickets === 1 ? "billettreservasjon" : "billettreservasjonar"} ventar på betaling`, sub: "Stadfest når Vipps er motteke", to: "/admin/billettar" },
  ].filter(Boolean);
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-8 w-8 rounded-lg" aria-label={`Varslingar${total ? `, ${total} ventar` : ""}`} data-testid="admin-topbar-notifications">
              <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
              {total > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-sol px-1 text-[10px] font-semibold leading-none text-ink tabular ring-2 ring-background" data-testid="admin-topbar-notifications-count">{total}</span>}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="bg-foreground text-background">Treng handling</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-80 rounded-2xl bg-popover p-0" data-testid="admin-notifications-popover">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5"><p className="text-sm font-medium">Treng handling</p><span className="t-mono-sm text-muted-foreground">{total === 0 ? "Ingenting" : `${total} ${total === 1 ? "sak" : "saker"}`}</span></div>
        {items.length === 0 ? <p className="px-4 py-6 text-center text-sm text-muted-foreground">Alt er handsama. Godt jobba.</p> : (
          <ul className="divide-y divide-border">
            {items.map((it) => (
              <li key={it.id}>
                <button type="button" onClick={() => go(it.to)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60" data-testid={`admin-notification-${it.id}`}>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sol/15 text-foreground"><it.icon className="h-4 w-4" strokeWidth={1.75} /></span>
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{it.title}</span><span className="block text-xs text-muted-foreground">{it.sub}</span></span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

const ThemeToggle = ({ theme, onToggle, className }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="ghost" size="icon" className={cn("h-8 w-8 rounded-lg", className)} onClick={onToggle} aria-label={theme === "dark" ? "Byt til lyst tema" : "Byt til mørkt tema"} aria-pressed={theme === "dark"} data-testid="admin-theme-toggle">
        {theme === "dark" ? <Sun className="h-[18px] w-[18px]" strokeWidth={1.75} /> : <Moon className="h-[18px] w-[18px]" strokeWidth={1.75} />}
      </Button>
    </TooltipTrigger>
    <TooltipContent className="bg-foreground text-background">{theme === "dark" ? "Lyst tema" : "Mørkt tema"}</TooltipContent>
  </Tooltip>
);

const SearchButton = ({ onClick, collapsed = false }) => (
  collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild><Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl text-paper/70 hover:bg-paper/[0.08] hover:text-paper" onClick={onClick} aria-label="Søk" data-testid="admin-search-button"><Search className="h-[18px] w-[18px]" strokeWidth={1.75} /></Button></TooltipTrigger>
      <TooltipContent side="right" className="bg-paper text-ink">Søk · ⌘K</TooltipContent>
    </Tooltip>
  ) : (
    <button type="button" onClick={onClick} className="flex h-10 w-full items-center gap-2.5 rounded-xl border border-paper/10 bg-paper/[0.06] px-3 text-left text-[13px] text-paper/60 transition-colors hover:bg-paper/[0.1] hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol/70" data-testid="admin-search-button">
      <Search className="h-4 w-4" strokeWidth={1.75} /> <span className="flex-1">Søk eller gå til…</span>
      <kbd className="rounded-md border border-paper/10 bg-paper/10 px-1.5 py-0.5 font-sans text-[10px] text-paper/60">⌘K</kbd>
    </button>
  )
);

const Topbar = ({ onOpenMobile, onOpenSearch, counts, theme, onToggleTheme }) => {
  const { pathname } = useLocation();
  const go = useGuardedNavigate();
  const current = currentNavItem(pathname);
  return (
    <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b border-border/50 bg-background/85 px-3 backdrop-blur-md sm:px-6 lg:px-8" data-testid="admin-topbar">
      <Button variant="ghost" size="icon" className="h-10 w-10 rounded-xl lg:hidden" aria-label="Opne meny" onClick={onOpenMobile} data-testid="admin-mobile-menu-button"><Menu className="h-5 w-5" /></Button>
      <nav aria-label="Brødsmular" className="flex min-w-0 items-center gap-1.5 text-[13px]" data-testid="admin-breadcrumb">
        <Link to="/admin" onClick={(e) => { e.preventDefault(); go("/admin"); }} className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline">Admin</Link>
        {current && current.to !== "/admin" && <ChevronRight className="hidden h-3.5 w-3.5 text-muted-foreground/70 sm:inline" aria-hidden="true" />}
        <span className="flex items-center gap-2 truncate font-medium" aria-current="page" data-testid="admin-breadcrumb-current">{current?.icon && <current.icon className="hidden h-4 w-4 text-muted-foreground sm:inline" strokeWidth={1.75} aria-hidden="true" />}{current?.label || "Admin"}</span>
      </nav>
      <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg lg:hidden" aria-label="Søk" onClick={onOpenSearch} data-testid="admin-search-button-mobile"><Search className="h-[18px] w-[18px]" /></Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button className="h-8 gap-1.5 rounded-lg px-3 text-[13px]" data-testid="admin-quick-create-button"><Plus className="h-4 w-4" /> <span className="hidden sm:inline">Ny</span></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 rounded-2xl bg-popover">
            <DropdownMenuLabel className="t-mono-sm font-medium text-muted-foreground">Opprett</DropdownMenuLabel>
            {QUICK_ACTIONS.map((a) => <DropdownMenuItem key={a.id} onSelect={() => go(a.to)} data-testid={`admin-quick-${a.id}`}><a.icon className="h-4 w-4" strokeWidth={1.75} /> {a.label}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <Notifications counts={counts} />
        <ThemeToggle theme={theme} onToggle={onToggleTheme} className="lg:hidden" />
        <Tooltip>
          <TooltipTrigger asChild><Button asChild variant="ghost" size="icon" className="hidden h-8 w-8 rounded-lg sm:inline-flex"><a href="/" target="_blank" rel="noreferrer" aria-label="Sjå nettsida" data-testid="admin-topbar-view-site"><ArrowUpRight className="h-[18px] w-[18px]" strokeWidth={1.75} /></a></Button></TooltipTrigger>
          <TooltipContent className="bg-foreground text-background">Sjå nettsida</TooltipContent>
        </Tooltip>
      </div>
    </header>
  );
};

const ShellInner = () => {
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; } });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light"; } catch { return "light"; } });
  const { pathname } = useLocation();
  const { data: dash } = useQuery({ queryKey: ["admin-dashboard"], queryFn: adminDashboard, refetchInterval: 60000 });
  const counts = useMemo(() => ({ bookings: dash?.new_bookings || 0, messages: dash?.unread_messages || 0, tickets: dash?.pending_tickets || 0, photocomments: dash?.new_photo_comments || 0 }), [dash]);

  useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ } }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [pathname]);
  // Theme is scoped to the admin shell – the public site keeps its own light palette.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
    return () => document.documentElement.classList.remove("dark");
  }, [theme]);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen((o) => !o); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const current = currentNavItem(pathname);
  const pageKey = pathname.split("/").slice(0, 3).join("/");
  // Captured element (not <Outlet/>) so the page that animates out keeps rendering the *old* route – otherwise the
  // leaving copy would mount the new page too, consume its `?ny=1` / `?rediger=` params and unmount again.
  const outlet = useOutlet();

  return (
    <TooltipProvider delayDuration={150}>
      <div className="admin-app min-h-screen lg:grid lg:grid-cols-[auto_minmax(0,1fr)]" data-testid="admin-shell" data-theme={theme}>
        <Seo title={current ? `${current.label} – Admin` : "Admin"} description="Administrasjon for styret i Halsnøy Kulturhus SA." noindex />

        {/* Desktop rail – sits on the tinted app background, no border */}
        <aside className={cn("admin-side sticky top-0 hidden h-screen flex-col transition-[width] duration-200 lg:flex", collapsed ? "w-[76px]" : "w-[268px]")} data-testid="admin-sidebar" data-collapsed={collapsed ? "true" : "false"}>
          <div aria-hidden="true" className="admin-rail-glow pointer-events-none absolute inset-x-0 top-0 h-72" />
          <div className={cn("relative shrink-0 space-y-3 pt-3", collapsed ? "px-3" : "px-3")}>
            <Workspace collapsed={collapsed} />
            <SearchButton collapsed={collapsed} onClick={() => setCmdOpen(true)} />
          </div>
          <ScrollArea className="min-h-0 flex-1">
            <div className="px-3 py-4"><SidebarNav counts={counts} collapsed={collapsed} variant="desktop" /></div>
          </ScrollArea>
          <div className={cn("shrink-0 space-y-1 p-3 pt-2", collapsed && "flex flex-col items-center")}>
            <div className={cn("mb-1 border-t border-paper/10 pt-2", collapsed && "w-full")}><SidebarNav counts={counts} collapsed={collapsed} variant="desktop" section="footer" /></div>
            <UserMenu collapsed={collapsed} />
            <div className={cn("flex items-center", collapsed ? "flex-col gap-1" : "justify-between")}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size={collapsed ? "icon" : "sm"} className={cn("rounded-xl text-paper/55 hover:bg-paper/[0.08] hover:text-paper", collapsed ? "h-9 w-9" : "h-9 justify-start gap-2 px-2")} onClick={() => setCollapsed((c) => !c)} aria-pressed={collapsed} aria-label={collapsed ? "Vis full meny" : "Minimer menyen"} data-testid="admin-sidebar-collapse-button">
                    {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.75} /> : <><PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={1.75} /> <span className="text-[12.5px]">Minimer</span></>}
                  </Button>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right" className="bg-paper text-ink">Vis full meny</TooltipContent>}
              </Tooltip>
              <ThemeToggle theme={theme} onToggle={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} className="text-paper/55 hover:bg-paper/[0.08] hover:text-paper" />
            </div>
          </div>
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="admin-app admin-side flex w-[300px] flex-col gap-0 border-paper/10 p-0 [&>button]:text-paper" data-testid="admin-mobile-nav">
            <SheetTitle className="sr-only">Admin-meny</SheetTitle>
            <div className="space-y-3 px-3 pr-12 pt-3"><Workspace onClick={() => setMobileOpen(false)} /><SearchButton onClick={() => { setMobileOpen(false); setCmdOpen(true); }} /></div>
            <ScrollArea className="flex-1"><div className="px-3 py-4"><SidebarNav counts={counts} onNavigate={() => setMobileOpen(false)} variant="mobile" /></div></ScrollArea>
            <div className="space-y-1 p-3 safe-bottom"><div className="border-t border-paper/10 pt-2"><SidebarNav counts={counts} onNavigate={() => setMobileOpen(false)} variant="mobile" section="footer" /></div><UserMenu onNavigate={() => setMobileOpen(false)} /></div>
          </SheetContent>
        </Sheet>

        {/* Inset canvas */}
        <div className="flex min-h-screen min-w-0 flex-col lg:p-2 lg:pl-0">
          <div className="admin-canvas flex min-h-full min-w-0 flex-1 flex-col overflow-x-hidden lg:rounded-2xl">
            <Topbar onOpenMobile={() => setMobileOpen(true)} onOpenSearch={() => setCmdOpen(true)} counts={counts} theme={theme} onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} />
            <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-5" data-testid="admin-main">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div key={pageKey} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}>
                  {outlet}
                </motion.div>
              </AnimatePresence>
            </main>
          </div>
        </div>

        <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
      </div>
    </TooltipProvider>
  );
};

export const AdminShell = () => (
  <DirtyProvider>
    <ShellInner />
  </DirtyProvider>
);
